import { useEffect, useState } from 'preact/hooks'
import { ClientAdmin, ErreurAdmin } from './api.ts'
import type { OptionSerieAdmin, SerieAdmin } from './types.ts'

const monnaie = (cents: number) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100)
const dateHeure = (valeur: string, fuseau: string) => new Intl.DateTimeFormat('fr-CA', { timeZone: fuseau, dateStyle: 'long', timeStyle: 'short' }).format(new Date(valeur))
const heure = (valeur: string, fuseau: string) => new Intl.DateTimeFormat('fr-CA', { timeZone: fuseau, timeStyle: 'short' }).format(new Date(valeur))

export function SeriesPane({ client, dateInitiale, fuseau, connexion, creee }: {
  client: ClientAdmin
  dateInitiale: string
  fuseau: string
  connexion(): void
  creee(date: string): void
}) {
  const [services, setServices] = useState<OptionSerieAdmin[]>([])
  const [serviceCle, setServiceCle] = useState('')
  const [ressourceCle, setRessourceCle] = useState('')
  const [date, setDate] = useState(dateInitiale)
  const [debut, setDebut] = useState('09:00')
  const [fin, setFin] = useState('10:00')
  const [repetitions, setRepetitions] = useState(2)
  const [nom, setNom] = useState('')
  const [courriel, setCourriel] = useState('')
  const [organisation, setOrganisation] = useState('')
  const [offres, setOffres] = useState<Record<string, number>>({})
  const [erreur, setErreur] = useState('')
  const [charge, setCharge] = useState(true)
  const [envoi, setEnvoi] = useState(false)
  const [serie, setSerie] = useState<SerieAdmin | null>(null)
  const [seriesExistantes, setSeriesExistantes] = useState<SerieAdmin[]>([])
  const [envoiContrat, setEnvoiContrat] = useState('')
  const [messageContrat, setMessageContrat] = useState('')
  useEffect(() => {
    const controleur = new AbortController()
    void Promise.all([client.optionsSeries(controleur.signal), client.listerSeries(controleur.signal)]).then(([options, historique]) => {
      if (!controleur.signal.aborted) {
        setServices(options.services)
        setSeriesExistantes(historique.series)
      }
    }).catch((cause) => {
      if (controleur.signal.aborted) return
      if (cause instanceof ErreurAdmin && cause.statut === 401) connexion()
      else setErreur(cause instanceof Error ? cause.message : 'Impossible de charger les salles.')
    }).finally(() => { if (!controleur.signal.aborted) setCharge(false) })
    return () => controleur.abort()
  }, [client])
  const selection = services.find((s) => s.cle === serviceCle)
  const soumettre = async (event: Event) => {
    event.preventDefault()
    if (envoi) return
    setErreur('')
    setSerie(null)
    if (!serviceCle || !ressourceCle || !date || !/^\d{2}:\d{2}$/.test(debut) || !/^\d{2}:\d{2}$/.test(fin) || fin <= debut || !Number.isInteger(repetitions) || repetitions < 2 || repetitions > 26 || nom.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(courriel.trim())) {
      setErreur('Indiquez le service, la salle, un créneau dans la même journée, de 2 à 26 semaines et les coordonnées du locataire.')
      return
    }
    setEnvoi(true)
    try {
      const resultat = await client.creerSerie({
        service_cle: serviceCle, ressource_cle: ressourceCle, date_locale: date,
        heure_debut: debut, heure_fin: fin, repetitions,
        locataire: { nom: nom.trim(), courriel: courriel.trim(), ...(organisation.trim() ? { organisation: organisation.trim() } : {}) },
        offres,
      })
      setSerie(resultat.serie)
      setSeriesExistantes((precedentes) => [resultat.serie, ...precedentes.filter((s) => s.id !== resultat.serie.id)])
      creee(date)
    } catch (cause) {
      if (cause instanceof ErreurAdmin && cause.statut === 401) connexion()
      else setErreur(cause instanceof Error ? cause.message : 'Impossible de créer la série.')
    } finally { setEnvoi(false) }
  }
  const envoyerContrat = async (existante: SerieAdmin) => {
    if (envoiContrat) return
    setEnvoiContrat(existante.id); setErreur(''); setMessageContrat('')
    try {
      const envoi = await client.envoyerContratSerieDemo(existante.id)
      setMessageContrat(`Contrat de ${existante.locataire.nom} placé dans la boîte d’envoi simulée pour ${envoi.destinataire}. Aucun courriel réel expédié.`)
    } catch (cause) {
      if (cause instanceof ErreurAdmin && cause.statut === 401) connexion()
      else setErreur(cause instanceof Error ? cause.message : 'Envoi du contrat impossible.')
    } finally { setEnvoiContrat('') }
  }
  return <div class="admin__section">
    <h2>Réservation hebdomadaire par l’administration</h2>
    <p class="admin__note">Chaque date devient une demande autonome. Le document produit est un projet non signé : aucune facture consolidée, aucun paiement ni signature de série ne sont enregistrés.</p>
    {charge ? <p role="status">Chargement des services publiés…</p> : null}
    {erreur ? <p role="alert" class="admin__message admin__message--erreur">{erreur}</p> : null}
    {messageContrat ? <p role="status" class="admin__message">{messageContrat}</p> : null}
    <form onSubmit={soumettre}>
      <div class="admin__filtres">
        <div class="admin__champ"><label for="serie-service">Service publié</label><select id="serie-service" required value={serviceCle} onChange={(e) => { setServiceCle(e.currentTarget.value); setRessourceCle(''); setOffres({}); setSerie(null) }}><option value="">Choisir</option>{services.map((s) => <option key={s.cle} value={s.cle}>{s.nom}</option>)}</select></div>
        <div class="admin__champ"><label for="serie-ressource">Salle ou terrain</label><select id="serie-ressource" required value={ressourceCle} onChange={(e) => { setRessourceCle(e.currentTarget.value); setSerie(null) }}><option value="">Choisir</option>{selection?.ressources.map((r) => <option key={r.cle} value={r.cle}>{r.nom}</option>)}</select></div>
        <div class="admin__champ"><label for="serie-date">Première date</label><input id="serie-date" type="date" required value={date} onInput={(e) => { setDate(e.currentTarget.value); setSerie(null) }} /></div>
        <div class="admin__champ"><label for="serie-debut">Début ({selection?.fuseau ?? fuseau})</label><input id="serie-debut" type="time" required value={debut} onInput={(e) => { setDebut(e.currentTarget.value); setSerie(null) }} /></div>
        <div class="admin__champ"><label for="serie-fin">Fin ({selection?.fuseau ?? fuseau})</label><input id="serie-fin" type="time" required value={fin} onInput={(e) => { setFin(e.currentTarget.value); setSerie(null) }} /></div>
        <div class="admin__champ"><label for="serie-repetitions">Nombre de semaines</label><input id="serie-repetitions" type="number" min="2" max="26" step="1" required value={repetitions} onInput={(e) => { setRepetitions(Number(e.currentTarget.value)); setSerie(null) }} /></div>
        <div class="admin__champ"><label for="serie-nom">Nom du locataire</label><input id="serie-nom" required minLength={2} value={nom} onInput={(e) => { setNom(e.currentTarget.value); setSerie(null) }} /></div>
        <div class="admin__champ"><label for="serie-courriel">Courriel</label><input id="serie-courriel" type="email" required value={courriel} onInput={(e) => { setCourriel(e.currentTarget.value); setSerie(null) }} /></div>
        <div class="admin__champ"><label for="serie-organisation">Entreprise ou organisme (facultatif)</label><input id="serie-organisation" value={organisation} onInput={(e) => { setOrganisation(e.currentTarget.value); setSerie(null) }} /></div>
        {selection?.offres.map((option) => <div class="admin__champ" key={option.cle}><label for={`serie-offre-${option.cle}`}>{option.nom} — {monnaie(option.montant_cents)} par date</label><input id={`serie-offre-${option.cle}`} type="number" min="0" max={option.quantite_maximum} step="1" value={offres[option.cle] ?? 0} onInput={(e) => { setOffres((precedentes) => ({ ...precedentes, [option.cle]: Number(e.currentTarget.value) })); setSerie(null) }} /></div>)}
      </div>
      <button type="submit" disabled={envoi || charge || !services.length || Boolean(serie)}>{envoi ? 'Création…' : 'Créer toutes les réservations'}</button>
    </form>
    {serie ? <div role="status" class="admin__section">
      <h3>{serie.occurrences.length} dates réservées — {monnaie(serie.montant_total_cents)} de tarifs indicatifs</h3>
      <p class="admin__note">Projet de contrat NON SIGNÉ, sans paiement ni facture consolidée. Vérifiez les clauses et le dépôt avant tout envoi.</p>
      <ol>{serie.occurrences.map((o) => <li key={o.demande_id}>{serie.nom_ressource} — {dateHeure(o.debut_utc, selection?.fuseau ?? fuseau)} à {heure(o.fin_utc, selection?.fuseau ?? fuseau)} — {monnaie(o.montant_cents)}</li>)}</ol>
      <a class="admin__secondaire" href={client.contratSerieUrl(serie.id)} download>Télécharger le projet PDF non signé</a>
    </div> : null}
    {seriesExistantes.length ? <div class="admin__section">
      <h3>Séries existantes et contrats</h3>
      <p class="admin__note">Le PDF d’une série administrative est un projet non signé. Seule une série citoyenne confirmée possède un contrat signé envoyable ici, en simulation.</p>
      <ul>{seriesExistantes.map((existante) => <li key={existante.id}>
        <strong>{existante.locataire.nom}</strong> — {existante.nom_ressource} — {existante.occurrences.length} dates — {monnaie(existante.montant_total_cents)}
        {' · '}{existante.origine === 'citoyen' ? `citoyen, ${existante.etat ?? 'en attente'}` : 'administration, non signé'}
        {' · '}<a href={client.contratSerieUrl(existante.id)} download>{existante.origine === 'citoyen' && existante.signee_le_utc ? 'Contrat signé PDF' : 'Projet PDF'}</a>
        {existante.origine === 'citoyen' && existante.etat === 'confirmee' && existante.signee_le_utc
          ? <button type="button" class="admin__secondaire" disabled={Boolean(envoiContrat)} onClick={() => void envoyerContrat(existante)}>{envoiContrat === existante.id ? 'Envoi…' : 'Envoyer le contrat (simulé)'}</button>
          : null}
      </li>)}</ul>
    </div> : null}
  </div>
}
